import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "./api/axios";
import { getImageUrl } from "./utils/image";
import { useActivityLogger } from "./hooks/useActivity";
import { useAuth } from "./contexts/AuthContext";
import { CircleCheck, EllipsisVertical, Pencil, Trash } from "lucide-react";

// Import the modals
import {
  EditThreadModal,
  DeleteThreadModal,
} from "./components/modals/ThreadModals";

export default function ForumTopic() {
  const { user, loading: loadingUser } = useAuth();
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { logUserActivity, loading: userLoading } = useActivityLogger();
  const [threads, setThreads] = useState([]);
  const [categoryName, setCategoryName] = useState("Forum");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [usePseudonym, setUsePseudonym] = useState(false);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newUsePseudonym, setNewUsePseudonym] = useState(false);
  const [isPosting, setIsPosting] = useState(false);

  // Edit/Delete modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [userRoles, setUserRoles] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [categoriesResponse, threadsResponse] = await Promise.all([
          api.get("/forum/categories"),
          api.get(`/forum/threads?category_id=${topicId}`),
        ]);

        const currentCategory = categoriesResponse.data.find(
          (cat) => cat.id === parseInt(topicId)
        );
        setCategoryName(currentCategory ? currentCategory.name : "Forum");

        setThreads(threadsResponse.data || []);
        if (!userLoading) {
          logUserActivity(
            "user",
            "view_forum_topic",
            "ForumTopic",
            `User viewed topic ${currentCategory ? currentCategory.name : "Unknown"}`
          );
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to load forum data");
        if (!userLoading) {
          logUserActivity(
            "error",
            "fetch_forum_topic_error",
            "ForumTopic",
            `Error fetching topic ${topicId}: ${err.message}`
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (topicId) {
      fetchData();
    }
  }, [topicId, userLoading]);

  const alertNotLoggedIn = () => {
    alert("Anda harus login untuk melakukan tindakan ini.");
    logUserActivity(
      "alert",
      "not_logged_in",
      "forum_topic",
      "user_not_logged_in_action"
    );
  };

  const navigateToDiscussion = (threadId) => {
    logUserActivity(
      "click",
      "thread_click",
      "forum_topic",
      `navigate_to_discussion thread_id=${threadId}`
    );
    navigate(`/forum/${topicId}/discussion/${threadId}`);
  };

  const formatTime = (dateString) => {
    if (!dateString) return "Tanggal tidak diketahui";

    const date = new Date(dateString);
    const now = new Date();

    // Reset time to compare dates only
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const articleDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const diffTime = today - articleDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    const months = [
      "Januari",
      "Februari",
      "Maret",
      "April",
      "Mei",
      "Juni",
      "Juli",
      "Agustus",
      "September",
      "Oktober",
      "November",
      "Desember",
    ];

    const days = [
      "Minggu",
      "Senin",
      "Selasa",
      "Rabu",
      "Kamis",
      "Jumat",
      "Sabtu",
    ];

    // Jika hari ini, tampilkan jam saja
    if (diffDays === 0) {
      return date.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }

    // Jika dalam seminggu terakhir, tampilkan hari dan tanggal
    if (diffDays > 0 && diffDays <= 7) {
      const dayName = days[date.getDay()];
      const day = date.getDate();
      const month = months[date.getMonth()];
      return `${dayName}, ${day} ${month}`;
    }

    // Jika tahun yang sama, tampilkan tanggal bulan saja
    if (date.getFullYear() === now.getFullYear()) {
      const day = date.getDate();
      const month = months[date.getMonth()];
      return `${day} ${month}`;
    }

    // Jika tahun berbeda, tampilkan dengan tahun
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getAuthorName = (thread) => {
    if (thread.user && thread.user.ID !== 0) {
      if (thread.use_pseudonym && thread.user.nama_samaran) {
        return thread.user.nama_samaran;
      }
      return thread.user.username || "Pengguna";
    }
    return "Anonim";
  };

  const getAuthorId = (thread) => {
    return thread.user ? thread.user.ID : null;
  };

  const isThread15minutesOldOrLess = (thread) => {
    const createdAt = new Date(thread.CreatedAt);
    const now = new Date();
    const diffInMinutes = (now - createdAt) / (1000 * 60);
    return diffInMinutes <= 15;
  };

  const getIsUserVerified = (thread) => {
    return thread.user && thread.user.is_verified;
  };

  useEffect(() => {
    const fetchUserRoles = async (allThreads) => {
      const roles = {};

      // Create a Set to avoid duplicate API calls for the same user
      const uniqueUserIds = new Set();
      allThreads.forEach((thread) => {
        if (thread.user?.ID) {
          uniqueUserIds.add(thread.user.ID);
        }
      });

      console.log("Unique user IDs to fetch:", Array.from(uniqueUserIds));

      // Fetch roles for each unique user
      for (const userId of uniqueUserIds) {
        try {
          console.log("Fetching data for user ID:", userId);
          const response = await api.get(`/users/${userId}/roles`);
          console.log("Response for user", userId, ":", response.data);

          // Make sure we're accessing the correct field
          const userRole = response.data[0]?.name || response.data?.name || "";
          roles[userId] = userRole;

          console.log("Set role for user", userId, ":", userRole);
        } catch (error) {
          console.error("Failed to fetch user data for ID", userId, ":", error);
          roles[userId] = "";
        }
      }

      console.log("Final roles object:", roles);
      setUserRoles(roles);
    };

    if (threads.length > 0) {
      fetchUserRoles(threads);
    }
  }, [threads]);

  const truncateContent = (content, maxLength = 150) => {
    if (!content) return "";
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  // Edit thread functions
  const openEditModal = (thread) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    setSelectedThread(thread);
    setShowEditModal(true);
    logUserActivity(
      "click",
      "open_edit_thread_modal",
      "forum_topic",
      `open_edit_modal thread_id=${thread.ID}`
    );
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedThread(null);
    setIsEditing(false);
    logUserActivity(
      "click",
      "close_edit_thread_modal",
      "forum_topic",
      "close_edit_modal"
    );
  };

  const handleEditSave = async ({
    title,
    content,
    use_pseudonym,
    imageFile,
  }) => {
    if (!selectedThread) return;

    try {
      setIsEditing(true);

      const formData = new FormData();
      formData.append("title", title);
      formData.append("content", content);
      formData.append("use_pseudonym", use_pseudonym.toString());
      if (imageFile) {
        formData.append("image", imageFile);
      }

      await api.put(`/forum/threads/${selectedThread.ID}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      logUserActivity(
        "submit",
        "edit_thread_form",
        "forum_topic",
        `edit_thread thread_id=${selectedThread.ID}`
      );

      // Refresh threads
      const threadsResponse = await api.get(
        `/forum/threads?category_id=${topicId}`
      );
      setThreads(threadsResponse.data || []);

      closeEditModal();
    } catch (err) {
      console.error("Failed to edit thread:", err);
      logUserActivity(
        "error",
        "edit_thread_form",
        "forum_topic",
        `Failed to edit thread ${selectedThread.ID}: ${err.message}`
      );
      alert("Gagal mengedit thread. Silakan coba lagi.");
    } finally {
      setIsEditing(false);
    }
  };

  // Delete thread functions
  const openDeleteModal = (thread) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    setSelectedThread(thread);
    setShowDeleteModal(true);
    logUserActivity(
      "click",
      "open_delete_thread_modal",
      "forum_topic",
      `open_delete_modal thread_id=${thread.ID}`
    );
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedThread(null);
    setIsDeleting(false);
    logUserActivity(
      "click",
      "close_delete_thread_modal",
      "forum_topic",
      "close_delete_modal"
    );
  };

  const handleDeleteConfirm = async () => {
    if (!selectedThread) return;

    try {
      setIsDeleting(true);

      await api.delete(`/forum/threads/${selectedThread.ID}`);

      logUserActivity(
        "click",
        "delete_thread_button",
        "forum_topic",
        `delete_thread thread_id=${selectedThread.ID}`
      );

      // Remove deleted thread from state
      setThreads((prevThreads) =>
        prevThreads.filter((thread) => thread.ID !== selectedThread.ID)
      );

      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete thread:", err);
      logUserActivity(
        "error",
        "delete_thread_button",
        "forum_topic",
        `delete_thread error for thread_id=${selectedThread.ID}: ${err.message}`
      );
      alert("Gagal menghapus thread. Silakan coba lagi.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ...existing reply, like, and create functions...

  const handleReplyClick = (e, threadId) => {
    e.stopPropagation();
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    logUserActivity(
      "click",
      "reply_button",
      "forum_topic",
      `reply to thread_id=${threadId}`
    );
    setReplyingTo(threadId);
    setReplyText("");
    setUsePseudonym(false);
  };

  const handleReplySubmit = async (e, threadId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    if (!replyText.trim()) return;

    try {
      setIsSubmittingReply(true);

      const formData = new FormData();
      formData.append("content", replyText);
      formData.append("use_pseudonym", usePseudonym.toString());

      await api.post(`/forum/threads/${threadId}/reply`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      logUserActivity(
        "submit",
        "reply_form",
        "forum_topic",
        `post_reply thread_id=${threadId}`
      );

      const response = await api.get(`/forum/threads?category_id=${topicId}`);
      setThreads(response.data || []);

      setReplyingTo(null);
      setReplyText("");
      setUsePseudonym(false);

      navigate(`/forum/${topicId}/discussion/${threadId}`);
    } catch (err) {
      console.error("Failed to post reply:", err);
      logUserActivity(
        "error",
        "reply_form",
        "forum_topic",
        `post_reply error for thread_id=${threadId}: ${err.message}`
      );
      if (err.response?.status === 401) {
        alert("You need to login to post a reply.");
      } else {
        alert("Failed to post reply. Please try again.");
      }
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleCancelReply = (e) => {
    e.stopPropagation();
    logUserActivity(
      "click",
      "cancel_reply",
      "forum_topic",
      `cancel_reply thread_id=${replyingTo}`
    );
    setReplyingTo(null);
    setReplyText("");
    setUsePseudonym(false);
  };

  const handleLikeClick = async (e, threadId) => {
    e.stopPropagation();
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    try {
      const response = await api.post(`/forum/threads/${threadId}/like`);
      const { is_liked } = response.data;

      setThreads((prevThreads) =>
        prevThreads.map((thread) =>
          thread.ID === threadId
            ? {
                ...thread,
                is_liked: is_liked,
                like_count: is_liked
                  ? (thread.like_count || 0) + 1
                  : Math.max((thread.like_count || 0) - 1, 0),
              }
            : thread
        )
      );

      logUserActivity(
        "click",
        "like_button",
        "forum_topic",
        is_liked ? "like" : "unlike"
      );
    } catch (err) {
      console.error("Failed to toggle like:", err);
      logUserActivity(
        "error",
        "like_button",
        "forum_topic",
        `toggle_like error for thread ${threadId}: ${err.message}`
      );

      if (err.response?.status === 401) {
        alert("You need to login to like posts.");
      }
    }
  };

  const openCreateModal = () => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    logUserActivity(
      "click",
      "create_thread_button",
      "forum_topic",
      `open_create_modal in topic ${topicId}`
    );
    setShowCreateModal(true);
    setNewTitle("");
    setNewContent("");
    setNewImage(null);
    setNewImagePreview(null);
    setNewUsePseudonym(false);
  };

  const closeCreateModal = () => {
    logUserActivity(
      "click",
      "close_create_thread_modal",
      "forum_topic",
      `close_create_modal in topic ${topicId}`
    );
    setShowCreateModal(false);
    setIsPosting(false);
  };

  const handleNewImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setNewImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removeNewImage = () => {
    logUserActivity(
      "click",
      "remove_new_image",
      "forum_topic",
      `remove_new_image in topic ${topicId}`
    );
    setNewImage(null);
    setNewImagePreview(null);
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;
    setIsPosting(true);
    try {
      const formData = new FormData();
      formData.append("title", newTitle);
      formData.append("content", newContent);
      formData.append("use_pseudonym", newUsePseudonym.toString());
      if (newImage) formData.append("image", newImage);

      await api.post("/forum/threads", formData, {
        params: { category_id: topicId },
        headers: { "Content-Type": "multipart/form-data" },
      });

      logUserActivity(
        "submit",
        "create_thread_form",
        "forum_topic",
        `create_thread in topic ${topicId}`
      );

      const threadsResponse = await api.get(
        `/forum/threads?category_id=${topicId}`
      );
      setThreads(threadsResponse.data || []);
      closeCreateModal();
    } catch (err) {
      console.error("Failed to create thread:", err);
      logUserActivity(
        "error",
        "create_thread_form",
        "forum_topic",
        `Failed to create thread in topic ${topicId}: ${err.message}`
      );
      alert("Gagal membuat thread baru.");
      setIsPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              logUserActivity(
                "click",
                "back_button",
                "forum_topic",
                "navigate_back"
              );
              navigate("/forum");
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Loading...</h1>
          <div className="h-6 w-6" />
        </div>

        <div className="flex items-center justify-center mt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading forum data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              logUserActivity(
                "click",
                "back_button",
                "forum_topic",
                "navigate_back"
              );
              navigate("/forum");
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Forum</h1>
          <div className="h-6 w-6" />
        </div>

        <div className="flex items-center justify-center mt-20">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Edit Thread Modal */}
      <EditThreadModal
        isOpen={showEditModal}
        onClose={closeEditModal}
        onSave={handleEditSave}
        loading={isEditing}
        initialTitle={selectedThread?.title || ""}
        initialContent={selectedThread?.content || ""}
        initialUsePseudonym={selectedThread?.use_pseudonym || false}
        initialImageUrl={
          selectedThread?.image_url ? getImageUrl(selectedThread.image_url) : ""
        }
      />

      {/* Delete Thread Modal */}
      <DeleteThreadModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
        threadTitle={selectedThread?.title || ""}
      />

      {/* Create Thread Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg mx-4 relative">
            <button
              onClick={closeCreateModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-lg font-semibold mb-4">Buat Thread Baru</h2>
            <form onSubmit={handleCreateThread}>
              <input
                type="text"
                placeholder="Judul thread"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="w-full mb-3 p-2 border rounded focus:outline-none"
                maxLength={100}
                required
              />
              <textarea
                placeholder="Isi thread"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                className="w-full mb-3 p-2 border rounded focus:outline-none"
                rows={4}
                required
              />
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="file"
                  accept="image/*"
                  id="new-thread-image"
                  className="hidden"
                  onChange={handleNewImage}
                />
                <label
                  htmlFor="new-thread-image"
                  className="cursor-pointer px-3 py-1 border rounded text-sm text-blue-600 hover:bg-blue-50"
                >
                  Upload Gambar
                </label>
                {newImagePreview && (
                  <div className="flex items-center gap-2">
                    <img
                      src={newImagePreview}
                      alt="Preview"
                      className="w-12 h-12 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={removeNewImage}
                      className="text-red-500 text-xs"
                    >
                      Hapus
                    </button>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 mb-4 text-sm">
                <input
                  type="checkbox"
                  checked={newUsePseudonym}
                  onChange={(e) => setNewUsePseudonym(e.target.checked)}
                  className="w-4 h-4"
                />
                Gunakan nama samaran
              </label>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 rounded bg-gray-200 text-gray-700"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isPosting || !newTitle.trim() || !newContent.trim()}
                  className="px-4 py-2 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300"
                >
                  {isPosting ? "Posting..." : "Posting"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-white px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              logUserActivity(
                "click",
                "back_button",
                "forum_topic",
                "navigate_back"
              );
              navigate("/forum");
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">{categoryName}</h1>
          <div className="h-6 w-6" />
        </div>

        {/* Posts */}
        <div className="space-y-4 pb-20">
          {threads.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="mb-6">
                <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Belum Ada Diskusi
                </h3>
                <p className="text-gray-500 mb-1 leading-relaxed">
                  Belum ada thread di kategori{" "}
                  <span className="font-medium">{categoryName}</span>
                </p>
                <p className="text-sm text-gray-400">
                  Jadilah yang pertama memulai diskusi menarik!
                </p>
              </div>

              <button
                onClick={openCreateModal}
                className="inline-flex items-center justify-center px-6 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Buat Thread Pertama
              </button>
            </div>
          ) : (
            threads.map((thread) => (
              <div
                key={thread.ID}
                className="border rounded-xl shadow-sm hover:bg-gray-50 transition"
              >
                <div className="relative">
                  {/* {getAuthorId(thread) === user?.ID &&
                    isThread15minutesOldOrLess(thread) && (
                      <div className="absolute top-3 right-3">
                        <button
                          className="p-2 rounded-full hover:bg-gray-100 transition"
                          onClick={(e) => {
                            e.stopPropagation();
                            setThreads((prev) =>
                              prev.map((t) =>
                                t.ID === thread.ID
                                  ? { ...t, showActions: !t.showActions }
                                  : t
                              )
                            );
                          }}
                          aria-label="More actions"
                        >
                          <EllipsisVertical className="w-5 h-5 text-gray-400" />
                        </button>
                        {thread.showActions && (
                          <div className="absolute right-0 mt-2 w-32 bg-white border rounded shadow-lg z-10">
                            <button
                              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(thread);
                                setThreads((prev) =>
                                  prev.map((t) =>
                                    t.ID === thread.ID
                                      ? { ...t, showActions: false }
                                      : t
                                  )
                                );
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteModal(thread);
                                setThreads((prev) =>
                                  prev.map((t) =>
                                    t.ID === thread.ID
                                      ? { ...t, showActions: false }
                                      : t
                                  )
                                );
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )} */}
                  <div
                    onClick={() => navigateToDiscussion(thread.ID)}
                    className="cursor-pointer p-4 pb-3 rounded-xl transition"
                  >
                    <h2 className="font-semibold text-base mb-1 line-clamp-2 text-gray-900">
                      {thread.title}
                    </h2>
                    <div className="flex items-center text-xs text-gray-500 mb-2 gap-2">
                      <span>{getAuthorName(thread)}</span>
                      {getIsUserVerified(thread) && (
                        <CircleCheck className="w-4 h-4 text-blue-500" />
                      )}
                      {userRoles[thread.user?.ID] && (
                        <>
                          <span>•</span>
                          <div className="bg-slate-100 px-1 py-0.5 rounded-2xl">
                            <span className="text-black">
                              {/* Add debugging */}
                              {console.log(
                                "Displaying role for user",
                                thread.user?.ID,
                                ":",
                                userRoles[thread.user?.ID]
                              )}
                              {userRoles[thread.user.ID]}
                            </span>
                          </div>
                        </>
                      )}
                      <span>•</span>
                      <span>{formatTime(thread.CreatedAt)}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2 line-clamp-3">
                      {truncateContent(thread.content)}
                    </p>
                    {thread.image_url && (
                      <div className="mb-3">
                        <img
                          src={getImageUrl(thread.image_url)}
                          alt="Thread image"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-6 text-xs text-gray-500 px-4 pb-3">
                  <button
                    onClick={(e) => handleReplyClick(e, thread.ID)}
                    className="flex items-center gap-1 hover:text-blue-500 transition-colors p-1 rounded"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <span>{thread.reply_count || 0}</span>
                  </button>

                  <button
                    onClick={(e) => handleLikeClick(e, thread.ID)}
                    className={`flex items-center gap-1 hover:text-red-500 transition-colors p-1 rounded ${
                      thread.is_liked ? "text-red-500" : ""
                    }`}
                  >
                    <svg
                      className={`w-4 h-4 ${
                        thread.is_liked ? "fill-current" : ""
                      }`}
                      fill={thread.is_liked ? "currentColor" : "none"}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                    <span>{thread.like_count || 0}</span>
                  </button>

                  {getAuthorId(thread) === user?.ID &&
                    isThread15minutesOldOrLess(thread) && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(thread);
                          }}
                          className="flex items-center gap-1 hover:text-red-500 transition-colors p-1 rounded"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(thread);
                          }}
                          className="flex items-center gap-1 hover:text-blue-500 transition-colors p-1 rounded"
                        >
                          <Pencil className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                      </>
                    )}
                </div>

                {/* Quick reply form */}
                {replyingTo === thread.ID && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <form onSubmit={(e) => handleReplySubmit(e, thread.ID)}>
                      <div className="flex gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder={`Reply to ${getAuthorName(thread)}...`}
                            className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows="2"
                            autoFocus
                          />

                          <div className="flex items-center mt-3 mb-3">
                            <label className="flex items-center cursor-pointer">
                              <div className="relative">
                                <input
                                  type="checkbox"
                                  checked={usePseudonym}
                                  onChange={(e) =>
                                    setUsePseudonym(e.target.checked)
                                  }
                                  className="sr-only"
                                />
                                <div
                                  className={`w-4 h-4 border-2 rounded-sm flex items-center justify-center transition-colors ${
                                    usePseudonym
                                      ? "bg-blue-500 border-blue-500"
                                      : "border-gray-300 bg-white"
                                  }`}
                                >
                                  {usePseudonym && (
                                    <svg
                                      className="w-3 h-3 text-white"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  )}
                                </div>
                              </div>
                              <span className="ml-2 text-sm text-gray-600">
                                Gunakan nama samaran
                              </span>
                            </label>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={handleCancelReply}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                            <button
                              type="submit"
                              disabled={!replyText.trim() || isSubmittingReply}
                              className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              {isSubmittingReply ? "Posting..." : "Reply"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Floating Action Button */}
        <button
          onClick={openCreateModal}
          className="fixed bottom-6 right-6 bg-blue-500 text-white w-14 h-14 rounded-full shadow-lg hover:bg-blue-600 transition flex items-center justify-center z-10"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>
      </div>
    </>
  );
}
